import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/**
 * Canvas-based signature pad.
 *
 * Props:
 *   width         – canvas width  (default 480)
 *   height        – canvas height (default 180)
 *   onChange(url) – called with base64 PNG data-URL whenever the drawing changes,
 *                   or null when cleared
 *   disabled      – lock the canvas
 *
 * Ref methods:
 *   clear()       – wipe the canvas
 *   isEmpty()     – returns true when nothing has been drawn
 *   toDataURL()   – returns the base64 PNG string (or null if empty)
 */
const SignaturePad = forwardRef(function SignaturePad(
  { width = 480, height = 180, onChange, disabled = false },
  ref
) {
  const canvasRef   = useRef(null);
  const drawing     = useRef(false);
  const hasStrokes  = useRef(false);
  const lastPos     = useRef({ x: 0, y: 0 });

  // ── Expose imperative methods ──────────────────────────────
  useImperativeHandle(ref, () => ({
    clear:      () => clearCanvas(),
    isEmpty:    () => !hasStrokes.current,
    toDataURL:  () => hasStrokes.current ? canvasRef.current.toDataURL('image/png') : null,
  }));

  // ── Draw helpers ───────────────────────────────────────────
  const getCtx = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.strokeStyle = '#1a237e';
    return ctx;
  };

  const relPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    };
  };

  const startDraw = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    const pos = relPos(e, canvasRef.current);
    lastPos.current = pos;
    const ctx = getCtx();
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1, 0, Math.PI * 2);
    ctx.fill();
  }, [disabled]);

  const draw = useCallback((e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = getCtx();
    const pos = relPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    hasStrokes.current = true;
  }, [disabled]);

  const endDraw = useCallback((e) => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasStrokes.current && onChange) {
      onChange(canvasRef.current.toDataURL('image/png'));
    }
  }, [onChange]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasStrokes.current = false;
    if (onChange) onChange(null);
  }, [onChange]);

  // ── Wire events ────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Mouse
    canvas.addEventListener('mousedown',  startDraw);
    canvas.addEventListener('mousemove',  draw);
    canvas.addEventListener('mouseup',    endDraw);
    canvas.addEventListener('mouseleave', endDraw);
    // Touch
    canvas.addEventListener('touchstart', startDraw, { passive: false });
    canvas.addEventListener('touchmove',  draw,      { passive: false });
    canvas.addEventListener('touchend',   endDraw);
    return () => {
      canvas.removeEventListener('mousedown',  startDraw);
      canvas.removeEventListener('mousemove',  draw);
      canvas.removeEventListener('mouseup',    endDraw);
      canvas.removeEventListener('mouseleave', endDraw);
      canvas.removeEventListener('touchstart', startDraw);
      canvas.removeEventListener('touchmove',  draw);
      canvas.removeEventListener('touchend',   endDraw);
    };
  }, [startDraw, draw, endDraw]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Draw your signature below
        </Typography>
        {!disabled && (
          <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={clearCanvas}
            sx={{ textTransform: 'none', fontSize: 12 }}>
            Clear
          </Button>
        )}
      </Stack>
      <Box sx={{
        border: '2px dashed',
        borderColor: disabled ? 'grey.300' : 'primary.main',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: disabled ? 'grey.50' : '#fafafa',
        cursor: disabled ? 'default' : 'crosshair',
        touchAction: 'none',
        userSelect: 'none',
        width: '100%',
        aspectRatio: `${width} / ${height}`,
      }}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
      </Box>
      <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
        Sign using your mouse, trackpad, or touchscreen
      </Typography>
    </Box>
  );
});

export default SignaturePad;
